import { useSessionStore } from '@hub/hooks/states/useSessionStore';
import { useIbdRecommendation } from '@hub/free-form-hub/common/recommendations/ibd-recommendation/useIbdRecommendation';
import { useIgeRecommendation } from '@hub/free-form-hub/common/recommendations/ige-recommendation/useIgeRecommendation';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { App, getNbsAppForCategory } from '@hub/free-form-hub/common/utils';
import type { NbsResponse } from '@hub/free-form-hub/common/uploadFile/fetchNbsRecommendation';
import { fetchNbsRecommendation } from '@hub/free-form-hub/common/uploadFile/fetchNbsRecommendation';
import { useEffect, useState } from 'react';
import type { ParsedUrlQuery } from 'querystring';

export const useNbsRecommendation = (
  fileId: string,
  quantity: number,
  clientReady: boolean,
  query?: ParsedUrlQuery
) => {
  const [nbsResponse, setNbsResponse] = useState<NbsResponse>(undefined);
  const [error, setError] = useState<Error | null>(null);
  const { getIbdRecommendation, setIbdRecommendations, ibdRecommendation } = useIbdRecommendation(quantity);
  const { getIgeRecommendation, setIgeRecommendations, igeRecommendation } = useIgeRecommendation();
  const session = useSessionStore(state => state.session);
  const nbsStatus = query?.nbsStatus as string;
  const nbsAlreadyChecked = nbsStatus === 'checked';

  const resetRecommendations = (): void => {
    setIgeRecommendations(null);
    setIbdRecommendations(null);
    setError(null);
  };

  const loadProductRecommendations = async (response: NbsResponse) => {
    if (!session) {
      return;
    }

    try {
      switch (getNbsAppForCategory(response)) {
        case App.ibd:
          await getIbdRecommendation(session, response);
          break;
        case App.ige:
          await getIgeRecommendation(session, response);
          break;
        default:
          break;
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load product recommendations'));
    }
  };

  const enabled = !!fileId && clientReady && !nbsAlreadyChecked;
  const queryKey = ['nbsRecommendation', fileId];

  const queryResult = useQuery<NbsResponse>({
    queryKey,
    queryFn: () => fetchNbsRecommendation(fileId),
    enabled,
    gcTime: 0,
  }) as UseQueryResult<NbsResponse>;

  useEffect(() => {
    if (queryResult.error) {
      setError(
        queryResult.error instanceof Error ? queryResult.error : new Error('Failed to fetch NBS recommendation')
      );
    }
  }, [queryResult.error]);

  useEffect(() => {
    if (queryResult.data) {
      setNbsResponse(queryResult.data);
    }
    if (queryResult.data && session) {
      void loadProductRecommendations(queryResult.data);
    }
  }, [queryResult.data, session]);

  return {
    ibdRecommendation,
    igeRecommendation,
    resetRecommendations,
    fileDimensions: nbsResponse?.dimensions,
    error,
    isLoading: queryResult.isLoading,
    nbsResponse,
  };
};
